import axios from 'axios';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { SettingsContext } from "@/utilities/settings-context";
import { PromotionType } from '@/states/promotion-state';

export const serviceNames = Object.freeze({
  ams: 'Arbetsförmedlingen',
  jobylon: 'Jobylon',
  teamtailor: 'TeamtailorWebhook',
  teamtailorXml: 'TeamtailorXml',
  teamtailorApi: 'TeamtailorApiService',
  ponty: 'Ponty',
  intenso: 'Intenso',
  jobtip: 'JobTip',
  recmanApi: 'RecmanApiService',
  haileyApi: 'HaileyApiService',
} as const);

export type ServiceName = typeof serviceNames[keyof typeof serviceNames];

type HasPromotionType = {
  defaultPromotionType: PromotionType | null;
};

type BaseService = {
  id: number;
  name: ServiceName;
  active: boolean;
  status: ConnectionStatus;
  required: boolean;
}

export type ServiceWithPromotionType = BaseService & HasPromotionType;

export type JobylonService = ServiceWithPromotionType & {
  name: typeof serviceNames.jobylon;
  referenceId: number | null;
};

export type TeamtailorService = ServiceWithPromotionType & {
  name: typeof serviceNames.teamtailor;
  integrationKey: string | null;
};

export type TeamtailorXmlService = ServiceWithPromotionType & {
  name: typeof serviceNames.teamtailorXml;
  companyUUId: string | null;
};

export type PontyService = ServiceWithPromotionType & {
  name: typeof serviceNames.ponty;
  feedUrl: string | null;
};

export type IntensoService = ServiceWithPromotionType & {
  name: typeof serviceNames.intenso;
  templateFilter: string | null;
};

export type TeamtailorApiServiceInfo = ServiceWithPromotionType & {
  name: typeof serviceNames.teamtailorApi;
  apiKey: string | null;
};

export type RecmanApiServiceInfo = ServiceWithPromotionType & {
  name: typeof serviceNames.recmanApi;
  apiKey: string | null;
};

export type HaileyApiServiceInfo = ServiceWithPromotionType & {
  name: typeof serviceNames.haileyApi;
};

type HaileyApiServiceMutationError = {
  error: 'access-token-conflict';
};

export type HaileyApiServiceMutationResult = HaileyApiServiceInfo | HaileyApiServiceMutationError;

type RecmanApiServiceMutationError = {
  error: 'api-key-conflict';
};

export type RecmanApiServiceMutationResult = RecmanApiServiceInfo | RecmanApiServiceMutationError;

export type JobTipService = ServiceWithPromotionType & {
  name: typeof serviceNames.jobtip;
};

export type AmsService = BaseService & {
  name: typeof serviceNames.ams;
  senderId: string | null;
};

export type ServiceInfo =
  | JobylonService
  | AmsService
  | TeamtailorService
  | TeamtailorXmlService
  | TeamtailorApiServiceInfo
  | PontyService
  | IntensoService
  | JobTipService
  | RecmanApiServiceInfo
  | HaileyApiServiceInfo;

export async function getServiceConnections(id: number): Promise<Array<ServiceInfo>> {

  return (await axios.get<Array<ServiceInfo>>(`${SettingsContext.getApiUrl('getServiceConnections')}?id=${id}`)).data;
}

export async function getServiceConnectionsMap(id: number): Promise<Record<ServiceName, ServiceInfo>> {

  return (await getServiceConnections(id)).reduce<Record<ServiceName, ServiceInfo>>((map, obj) => { map[obj.name] = obj; return map; }, {} as Record<ServiceName, ServiceInfo>);
}

export async function addJobylon(companyId: number, referenceId: number, admin = false) {
  try {
    const endpoint = admin ? 'addJobylonAdmin' : 'addJobylon';

    const response = await axios.post<JobylonService>(`${SettingsContext.getApiUrl(endpoint)}/${companyId}`, referenceId, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    return response.data;
  }
  catch {
    return null;
  }
}

export async function deleteJobylon(serviceId: number) {
  try {
    await axios.delete(`${SettingsContext.getApiUrl('removeJobylon')}/${serviceId}`);
    return true;
  }
  catch {
    return false;
  }
}

export async function enableTeamtailor(companyId: number) {
  try {
    return (await axios.post<TeamtailorService>(SettingsContext.getApiUrl('enableTeamtailor'), { id: companyId })).data;
  }
  catch {
    return null;
  }
}

export async function enableTeamtailorXml(companyId: number, companyUUId: string) {
  try {
    return (await axios.post<TeamtailorXmlService>(SettingsContext.getApiUrl('enableTeamtailorXml'), { id: companyId, companyUUId: companyUUId })).data;
  }
  catch {
    return null;
  }
}

export async function enableTeamtailorApi(companyId: number, apiKey: string) {
  try {
    return (await axios.post<TeamtailorApiServiceInfo>(SettingsContext.getApiUrl('enableTeamtailorApi'), { id: companyId, apiKey })).data;
  }
  catch {
    return null;
  }
}

export async function updateTeamtailorApi(companyId: number, apiKey: string) {
  try {
    return (await axios.post<TeamtailorApiServiceInfo>(SettingsContext.getApiUrl('updateTeamtailorApi'), { id: companyId, apiKey })).data;
  }
  catch {
    return null;
  }
}

export async function disableTeamtailorApi(companyId: number) {
  try {
    await axios.post(SettingsContext.getApiUrl('disableTeamtailorApi'), { id: companyId });
    return true;
  }
  catch {
    return false;
  }
}

export async function enableRecmanApi(companyId: number, apiKey: string): Promise<RecmanApiServiceMutationResult | null> {
  try {
    return (await axios.post<RecmanApiServiceInfo>(SettingsContext.getApiUrl('enableRecmanApi'), { id: companyId, apiKey })).data;
  }
  catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 400) {
      const errors = e.response.data?.errors as Record<string, string[]> | undefined;
      const message = errors
        ? Object.values(errors).flat().join(' ')
        : (e.response.data?.message as string | undefined);

      if (message?.toLowerCase().includes('api key') && message.toLowerCase().includes('already exists')) {
        return { error: 'api-key-conflict' } as RecmanApiServiceMutationResult;
      }
    }

    return null;
  }
}

export async function updateRecmanApi(companyId: number, apiKey: string): Promise<RecmanApiServiceMutationResult | null> {
  try {
    return (await axios.post<RecmanApiServiceInfo>(SettingsContext.getApiUrl('updateRecmanApi'), { id: companyId, apiKey })).data;
  }
  catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 400) {
      const errors = e.response.data?.errors as Record<string, string[]> | undefined;
      const message = errors
        ? Object.values(errors).flat().join(' ')
        : (e.response.data?.message as string | undefined);

      if (message?.toLowerCase().includes('api key') && message.toLowerCase().includes('already exists')) {
        return { error: 'api-key-conflict' } as RecmanApiServiceMutationResult;
      }
    }

    return null;
  }
}

export async function disableRecmanApi(companyId: number) {
  try {
    await axios.post(SettingsContext.getApiUrl('disableRecmanApi'), { id: companyId });
    return true;
  }
  catch {
    return false;
  }
}

const isHaileyTokenConflict = (error: unknown) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return false;
  }

  const errors = error.response.data?.errors as Record<string, string[]> | undefined;
  const message = errors
    ? Object.values(errors).flat().join(' ')
    : (error.response.data?.message as string | undefined);
  const normalized = message?.toLowerCase();

  return !!normalized?.includes('access token') && normalized.includes('already exists');
};

export async function enableHaileyApi(
  companyId: number,
  accessToken: string
): Promise<HaileyApiServiceMutationResult | null> {
  try {
    const response = await axios.post<HaileyApiServiceInfo>(
      SettingsContext.getApiUrl('enableHaileyApi'),
      {
        id: companyId,
        accessToken,
      }
    );

    return response.data;
  }
  catch (error) {
    return isHaileyTokenConflict(error) ? { error: 'access-token-conflict' } : null;
  }
}

export async function updateHaileyApi(
  companyId: number,
  accessToken: string
): Promise<HaileyApiServiceMutationResult | null> {
  try {
    const response = await axios.post<HaileyApiServiceInfo>(
      SettingsContext.getApiUrl('updateHaileyApi'),
      {
        id: companyId,
        accessToken,
      }
    );

    return response.data;
  }
  catch (error) {
    return isHaileyTokenConflict(error) ? { error: 'access-token-conflict' } : null;
  }
}

export async function disableHaileyApi(companyId: number) {
  try {
    await axios.post(SettingsContext.getApiUrl('disableHaileyApi'), { id: companyId });
    return true;
  }
  catch {
    return false;
  }
}

export async function disableTeamtailor(serviceId: number) {
  try {
    await axios.post(SettingsContext.getApiUrl('disableService'), { id: serviceId });

    return true;
  }
  catch {
    return false;
  }
}

export async function setDefaultPromotionType(serviceId: number, promotionType: PromotionType | null) {
  try {
    await axios.post(SettingsContext.getApiUrl('setDefaultPromotionType'), { id: serviceId, promotionType });

    return true;
  }
  catch {
    return null;
  }
}

export async function enablePonty(companyId: number, feedUrl: string) {
  try {
    return (await axios.post<PontyService>(SettingsContext.getApiUrl('enablePonty'), { id: companyId, feedUrl })).data;
  }
  catch {
    return null;
  }
}

export async function disablePonty(serviceId: number) {
  try {
    await axios.post(SettingsContext.getApiUrl('disableService'), { id: serviceId });

    return true;
  }
  catch {
    return false;
  }
}

export async function adminAddPonty(companyId: number, feedUrl: string) {
  try {
    return (await axios.post<PontyService>(SettingsContext.getApiUrl('adminAddPonty'), { id: companyId, feedUrl })).data;
  }
  catch {
    return null;
  }
}

export async function adminDeletePonty(serviceId: number) {
  try {
    await axios.delete(`${SettingsContext.getApiUrl('adminDeletePonty')}/${serviceId}`);

    return true;
  }
  catch {
    return false;
  }
}

export async function enableIntenso(companyId: number, templateFilter: string) {
  try {
    return (await axios.post<IntensoService>(SettingsContext.getApiUrl('enableIntenso'), { id: companyId, templateFilter })).data;
  }
  catch (e) {
    if (axios.isAxiosError(e) && e?.response?.status === 409) {
      // signal uniqueness violation
      return { error: 'conflict' } as unknown as IntensoService;
    }
    return null;
  }
}

export async function disableIntenso(serviceId: number) {
  try {
    await axios.delete(SettingsContext.getApiUrl('disableIntenso') + '/' + serviceId);
    return true;
  }
  catch {
    return false;
  }
}

export async function adminAddIntenso(companyId: number, templateFilter: string) {
  try {
    return (await axios.post<IntensoService>(SettingsContext.getApiUrl('adminAddIntenso'), { id: companyId, templateFilter })).data;
  }
  catch (e) {
    if (axios.isAxiosError(e) && e?.response?.status === 409) {
      return { error: 'conflict' } as unknown as IntensoService;
    }
    return null;
  }
}

export async function adminDeleteIntenso(serviceId: number) {
  try {
    await axios.delete(`${SettingsContext.getApiUrl('disableIntenso')}/${serviceId}`);
    return true;
  }
  catch {
    return false;
  }
}

export const defaultAmsService = (): AmsService => ({
  id: 0,
  name: serviceNames.ams,
  required: true,
  status: ConnectionStatus.notConnected,
  active: false,
  senderId: null,
});

export const defaultTeamtailorService = (): TeamtailorService => ({
  id: 0,
  name: serviceNames.teamtailor,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  integrationKey: null,
  defaultPromotionType: null,
});

export const defaultTeamtailorXmlService = (): TeamtailorXmlService => ({
  id: 0,
  name: serviceNames.teamtailorXml,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  companyUUId: null,
  defaultPromotionType: null,
});

export const defaultTeamtailorApiService = (): TeamtailorApiServiceInfo => ({
  id: 0,
  name: serviceNames.teamtailorApi,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  apiKey: null,
  defaultPromotionType: null,
});

export const defaultRecmanApiService = (): RecmanApiServiceInfo => ({
  id: 0,
  name: serviceNames.recmanApi,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  apiKey: null,
  defaultPromotionType: null,
});

export const defaultHaileyApiService = (): HaileyApiServiceInfo => ({
  id: 0,
  name: serviceNames.haileyApi,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  defaultPromotionType: null,
});

export const defaultPontyService = (): PontyService => ({
  id: 0,
  name: serviceNames.ponty,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  feedUrl: null,
  defaultPromotionType: null,
});

export const defaultIntensoService = (): IntensoService => ({
  id: 0,
  name: serviceNames.intenso,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  templateFilter: null,
  defaultPromotionType: null,
});

export const defaultJobylonService = (): JobylonService => ({
  id: 0,
  name: serviceNames.jobylon,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  referenceId: null,
  defaultPromotionType: null,
});

export async function adminAddJobTip(companyId: number) {
  try {
    return (await axios.post<JobTipService>(SettingsContext.getApiUrl('adminAddJobTip'), { id: companyId })).data;
  }
  catch {
    return null;
  }
}

export async function adminDeleteJobTip(serviceId: number) {
  try {
    await axios.delete(`${SettingsContext.getApiUrl('adminDeleteJobTip')}/${serviceId}`);
    return true;
  }
  catch {
    return false;
  }
}

export const defaultJobTipService = (): JobTipService => ({
  id: 0,
  name: serviceNames.jobtip,
  required: false,
  status: ConnectionStatus.notConnected,
  active: false,
  defaultPromotionType: null,
});

export async function toggleAmsService(companyId: number, required?: boolean, active?: boolean): Promise<AmsService | null> {
  try {
    const response = await axios.post<AmsService>(
      `${SettingsContext.getApiUrl('toggleAmf')}`,
      { 
        id: companyId,
        required,
        activated: active
      }
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function toggleJobFetchService(companyId: number, enable: boolean): Promise<boolean> {
  try {
    await axios.post(
      `${SettingsContext.getApiUrl('toggleJobFetchService')}`,
      { 
        id: companyId,
        enable: enable
      }
    );
    return enable;
  } catch {
    return !enable;
  }
}
